import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useAccount } from "@azure/msal-react";
import {
  fetchRefreshHealth,
  fetchRefreshSchedules,
  fetchSlaBreaches,
  acknowledgeSlaBreach,
  fetchDataflowRefreshes,
} from "../api/client";
import {
  RefreshRecord,
  RefreshScheduleConfig,
  SlaBreachRecord,
  DataflowRefreshRecord,
  Workspace,
} from "../types";
import ShowPersonalToggle from "../components/ShowPersonalToggle";
import useWorkspaceList from "../hooks/useWorkspaceList";
import StatusBadge from "../components/StatusBadge";
import SeverityBadge from "../components/SeverityBadge";
import StatCard from "../components/StatCard";
import DateRangePicker from "../components/DateRangePicker";
import ExportButton from "../components/ExportButton";
import RefreshCalendar from "../components/RefreshCalendar";
import DetailModal from "../components/DetailModal";
import { useDateFormat } from "../preferences/PreferencesProvider";
import { formatDateTime, formatRelative, utcDaysAgo, utcToday } from "../utils/datetime";

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

function today(): string {
  return utcToday();
}
function sevenDaysAgo(): string {
  return utcDaysAgo(7);
}
function formatDuration(seconds?: number): string {
  if (seconds == null) return "—";
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}
function formatMissedBy(minutes?: number): string {
  if (minutes == null) return "—";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m late`;
  return `${h}h ${m}m late`;
}

// ---------------------------------------------------------------------------
// Tab IDs
// ---------------------------------------------------------------------------

type TabId = "overview" | "history" | "schedules" | "sla-breaches" | "calendar" | "dataflows";

const TABS: { id: TabId; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "history", label: "Refresh History" },
  { id: "schedules", label: "Schedules" },
  { id: "sla-breaches", label: "SLA Breaches" },
  { id: "calendar", label: "Calendar" },
  { id: "dataflows", label: "Dataflows" },
];

// ---------------------------------------------------------------------------
// Loading spinner
// ---------------------------------------------------------------------------

function Spinner() {
  return (
    <div className="flex items-center justify-center py-16 text-gray-400">
      <svg className="w-6 h-6 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
      </svg>
      Loading…
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error banner
// ---------------------------------------------------------------------------

function ErrorBanner({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-center justify-between">
      <span>{message}</span>
      <button onClick={onRetry} className="ml-4 text-red-600 underline text-xs">Retry</button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error details expander (for refresh history)
// ---------------------------------------------------------------------------

/**
 * Opens a refresh failure's error payload in a dialog.
 *
 * It used to unfold inside the table cell it sits in, which was capped at
 * `max-w-sm` — so a pretty-printed Power BI error, the widest thing on the
 * page, was rendered in the narrowest available column and had to be scrolled
 * in two directions to read. The dialog gets it the full width, and the table
 * no longer reflows around whichever row happens to be open.
 */
function ErrorExpander({ message }: { message?: string }) {
  const [open, setOpen] = useState(false);
  if (!message) return <span className="text-gray-300">—</span>;

  let parsed: unknown = message;
  try { parsed = JSON.parse(message); } catch { /* use raw string */ }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="text-red-600 text-xs underline"
      >
        Show error
      </button>
      {open && (
        <DetailModal
          title="Refresh error"
          subtitle="As reported by the Power BI refresh history"
          width="lg"
          onClose={() => setOpen(false)}
        >
          <pre className="overflow-auto whitespace-pre-wrap rounded border border-danger-border bg-danger-subtle p-3 text-xs text-danger-fg">
            {typeof parsed === "object" ? JSON.stringify(parsed, null, 2) : String(parsed)}
          </pre>
        </DetailModal>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Overview tab
// ---------------------------------------------------------------------------

function OverviewTab({
  records,
  slaBreaches,
  dataflowRecords,
  onAcknowledge,
}: {
  records: RefreshRecord[];
  slaBreaches: SlaBreachRecord[];
  dataflowRecords: DataflowRefreshRecord[];
  onAcknowledge: (b: SlaBreachRecord) => void;
}) {
  const dateFormat = useDateFormat();
  const formatDate = (value?: string) => formatDateTime(value, dateFormat);

  const sevenAgo = sevenDaysAgo();

  const stats = useMemo(() => {
    const recent = records.filter((r) => (r.refreshStartTime ?? "") >= sevenAgo);
    const total = recent.length;
    const failed = recent.filter((r) => (r.refreshStatus ?? "").toLowerCase() === "failed");
    // null, not 100. No refresh rows means the collector has not reported,
    // and rendering that as a green "100%" is the most reassuring possible
    // presentation of having no evidence at all.
    const successRate =
      total === 0 ? null : Math.round(((total - failed.length) / total) * 100);
    const activeSlaBreaches = slaBreaches.filter((b) => !b.acknowledged).length;
    const dataflowFailed = dataflowRecords.filter(
      (r) => r.refreshStartTime >= sevenAgo && (r.refreshStatus ?? "").toLowerCase() === "failed"
    ).length;
    return { total, failedCount: failed.length, successRate, activeSlaBreaches, dataflowFailed };
  }, [records, slaBreaches, dataflowRecords, sevenAgo]);

  const recentFailures = useMemo(
    () => records.filter((r) => (r.refreshStatus ?? "").toLowerCase() === "failed").slice(0, 5),
    [records]
  );

  const unacknowledgedBreaches = useMemo(
    () => slaBreaches.filter((b) => !b.acknowledged).slice(0, 5),
    [slaBreaches]
  );

  return (
    <div className="space-y-5">
      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="Total Refreshes (7d)"
          value={stats.total}
          severity="normal"
        />
        <StatCard
          label="Success Rate (7d)"
          value={stats.successRate === null ? "—" : `${stats.successRate}%`}
          severity={
            stats.successRate === null
              ? "warning"
              : stats.successRate < 80
                ? "critical"
                : stats.successRate < 95
                  ? "warning"
                  : "normal"
          }
        />
        <StatCard
          label="Active SLA Breaches"
          value={stats.activeSlaBreaches}
          severity={stats.activeSlaBreaches > 0 ? "critical" : "normal"}
        />
        <StatCard
          label="Dataflow Failures (7d)"
          value={stats.dataflowFailed}
          severity={stats.dataflowFailed > 0 ? "warning" : "normal"}
        />
      </div>

      {/* Two-column */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Recent failures */}
        <div className="rounded-xl border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700">Recent Failures</h3>
          </div>
          {recentFailures.length === 0 ? (
            <p className="px-4 py-6 text-sm text-gray-400">No failures — all datasets refreshed successfully.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {recentFailures.map((r, i) => (
                <li key={i} className="px-4 py-3">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-gray-800 truncate max-w-xs">{r.datasetName}</span>
                    <span className="text-gray-400 text-xs ml-2 whitespace-nowrap">{formatRelative(r.refreshStartTime, dateFormat)}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">{r.workspaceName}</div>
                  {r.errorMessage && (
                    <div className="text-xs text-red-500 mt-1 truncate max-w-sm" title={r.errorMessage}>
                      {r.errorMessage.slice(0, 100)}{r.errorMessage.length > 100 ? "…" : ""}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* SLA breaches */}
        <div className="rounded-xl border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700">Unacknowledged SLA Breaches</h3>
          </div>
          {unacknowledgedBreaches.length === 0 ? (
            <p className="px-4 py-6 text-sm text-gray-400">No active SLA breaches.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {unacknowledgedBreaches.map((b, i) => (
                <li key={i} className="px-4 py-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="text-sm font-medium text-gray-800 truncate max-w-xs">{b.datasetName}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{b.workspaceName}</div>
                      <div className="text-xs text-orange-600 mt-0.5">
                        {formatMissedBy(b.missedByMinutes)} — expected by {formatDate(b.expectedRefreshBy)}
                      </div>
                    </div>
                    <button
                      onClick={() => onAcknowledge(b)}
                      className="ml-2 shrink-0 px-2 py-1 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
                    >
                      Ack
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Refresh History tab
// ---------------------------------------------------------------------------

function HistoryTab({ workspaces }: { workspaces: Workspace[] }) {
  const dateFormat = useDateFormat();
  const formatDate = (value?: string) => formatDateTime(value, dateFormat);

  const [records, setRecords] = useState<RefreshRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState(sevenDaysAgo());
  const [dateTo, setDateTo] = useState(today());
  const [workspaceFilter, setWorkspaceFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);
    const params: Record<string, string> = { limit: "1000" };
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (workspaceFilter) params.workspace_id = workspaceFilter;
    if (statusFilter) params.status = statusFilter;

    fetchRefreshHealth(params)
      .then((data) => setRecords(data as unknown as RefreshRecord[]))
      .catch((err) => setError(err.message ?? "Failed to load refresh history."))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo, workspaceFilter, statusFilter]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, []);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
        <div className="flex flex-wrap items-end gap-4">
          <DateRangePicker
            label="Date range:"
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateFromChange={setDateFrom}
            onDateToChange={setDateTo}
          />
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Workspace</label>
            <select
              value={workspaceFilter}
              onChange={(e) => setWorkspaceFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white w-48"
            >
              <option value="">All workspaces</option>
              {workspaces.map((ws) => (
                <option key={ws.workspaceId} value={ws.workspaceId}>{ws.name}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white w-32"
            >
              <option value="">All</option>
              <option value="Completed">Completed</option>
              <option value="Failed">Failed</option>
              <option value="Disabled">Disabled</option>
              {/* Power BI's word for a run that is still going. It is written
                  to the table like any other status, so it needs a way to be
                  isolated here. */}
              <option value="Unknown">In progress</option>
            </select>
          </div>
          <button
            onClick={fetchData}
            className="px-4 py-2 bg-blue-900 text-white text-sm font-medium rounded-lg hover:bg-blue-800 transition-colors self-end"
          >
            Apply
          </button>
          <div className="self-end ml-auto">
            <ExportButton
              endpoint="/api/export/refresh-history"
              params={{
                date_from: dateFrom,
                date_to: dateTo,
                workspace_id: workspaceFilter || undefined,
                status: statusFilter || undefined,
              }}
              filename="refresh-history.csv"
            />
          </div>
        </div>
      </div>

      {loading && <Spinner />}
      {error && !loading && <ErrorBanner message={error} onRetry={fetchData} />}
      {!loading && !error && (
        <div>
          <p className="text-sm text-gray-500 mb-3">{records.length} record{records.length !== 1 ? "s" : ""}</p>
          <div className="rounded-xl border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {["Dataset", "Workspace", "Started", "Status", "Duration", "Type", "Error"].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {records.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-gray-400">No refresh records found.</td>
                  </tr>
                )}
                {records.map((r, i) => {
                  const isFailed = (r.refreshStatus ?? "").toLowerCase() === "failed";
                  return (
                    <tr key={i} className={isFailed ? "bg-red-50" : "hover:bg-gray-50"}>
                      <td className="px-4 py-3 text-gray-800 font-medium">{r.datasetName}</td>
                      <td className="px-4 py-3 text-gray-500">{r.workspaceName}</td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{formatDate(r.refreshStartTime)}</td>
                      <td className="px-4 py-3"><StatusBadge status={r.refreshStatus ?? ""} /></td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{formatDuration(r.durationSeconds)}</td>
                      <td className="px-4 py-3 text-gray-500">{r.refreshType || "—"}</td>
                      <td className="px-4 py-3 max-w-xs">
                        <ErrorExpander message={r.errorMessage} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Schedules tab
// ---------------------------------------------------------------------------

function SchedulesTab({ workspaces }: { workspaces: Workspace[] }) {
  const dateFormat = useDateFormat();
  const formatDate = (value?: string) => formatDateTime(value, dateFormat);

  const [schedules, setSchedules] = useState<RefreshScheduleConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workspaceFilter, setWorkspaceFilter] = useState("");
  const [enabledOnly, setEnabledOnly] = useState(false);

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);
    const params: Record<string, string | boolean> = {};
    if (workspaceFilter) params.workspace_id = workspaceFilter;
    if (enabledOnly) params.enabled_only = true;

    fetchRefreshSchedules(params)
      .then(setSchedules)
      .catch((err) => setError(err.message ?? "Failed to load schedules."))
      .finally(() => setLoading(false));
  }, [workspaceFilter, enabledOnly]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, []);

  return (
    <div className="space-y-4">
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Workspace</label>
            <select
              value={workspaceFilter}
              onChange={(e) => setWorkspaceFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-700 bg-white w-48"
            >
              <option value="">All workspaces</option>
              {workspaces.map((ws) => (
                <option key={ws.workspaceId} value={ws.workspaceId}>{ws.name}</option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600 self-end pb-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={enabledOnly}
              onChange={(e) => setEnabledOnly(e.target.checked)}
              className="rounded"
            />
            Enabled only
          </label>
          <button
            onClick={fetchData}
            className="px-4 py-2 bg-blue-900 text-white text-sm font-medium rounded-lg hover:bg-blue-800 self-end"
          >
            Apply
          </button>
          <div className="self-end ml-auto">
            <ExportButton
              endpoint="/api/export/refresh-schedules"
              params={{ workspace_id: workspaceFilter || undefined }}
              filename="refresh-schedules.csv"
            />
          </div>
        </div>
      </div>

      {loading && <Spinner />}
      {error && !loading && <ErrorBanner message={error} onRetry={fetchData} />}
      {!loading && !error && (
        <div>
          <p className="text-sm text-gray-500 mb-3">{schedules.length} dataset{schedules.length !== 1 ? "s" : ""}</p>
          <div className="rounded-xl border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {["Dataset", "Workspace", "Enabled", "Days", "Times", "Timezone", "Next Expected", "SLA Window"].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {schedules.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-gray-400">No schedules found.</td>
                  </tr>
                )}
                {schedules.map((s, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-800">{s.datasetName}</td>
                    <td className="px-4 py-3 text-gray-500">{s.workspaceName}</td>
                    <td className="px-4 py-3">
                      {s.scheduleEnabled
                        ? <span className="text-green-600 font-bold">&#10003;</span>
                        : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{s.days.join(", ") || "—"}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{s.times.join(", ") || "—"}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{s.localTimeZoneId}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
                      {s.nextExpectedRefresh
                        ? <><span>{formatDate(s.nextExpectedRefresh)}</span><br /><span className="text-gray-400">{formatRelative(s.nextExpectedRefresh, dateFormat)}</span></>
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{s.slaWindowMinutes} min</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SLA Breaches tab
// ---------------------------------------------------------------------------

function SlaBreachesTab({ workspaces }: { workspaces: Workspace[] }) {
  // No signed-in identity is read here. It used to be, to pass as the
  // acknowledgement note; that stopped when the note went, and the variable
  // stayed behind — enough to fail a production build, which treats lint
  // warnings as errors.
  const dateFormat = useDateFormat();
  const formatDate = (value?: string) => formatDateTime(value, dateFormat);

  const [breaches, setBreaches] = useState<SlaBreachRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState(sevenDaysAgo());
  const [dateTo, setDateTo] = useState(today());
  const [workspaceFilter, setWorkspaceFilter] = useState("");
  const [severityFilter, setSeverityFilter] = useState("");
  const [ackFilter, setAckFilter] = useState<"all" | "acknowledged" | "unacknowledged">("all");
  const [ackLoading, setAckLoading] = useState<string | null>(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);
    const params: Record<string, string | boolean> = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (workspaceFilter) params.workspace_id = workspaceFilter;
    if (severityFilter) params.severity = severityFilter;
    if (ackFilter === "acknowledged") params.acknowledged = true;
    if (ackFilter === "unacknowledged") params.acknowledged = false;

    fetchSlaBreaches(params)
      .then(setBreaches)
      .catch((err) => setError(err.message ?? "Failed to load SLA breaches."))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo, workspaceFilter, severityFilter, ackFilter]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, []);

  async function handleAcknowledge(breach: SlaBreachRecord) {
    const key = `${breach.datasetId}|${breach.breachDetectedAt}`;
    setAckLoading(key);
    try {
      // No third argument. That parameter is the acknowledgement *note* — a
      // free-text justification stored in the evidence trail — and passing
      // `userEmail` filled every note with the acknowledger's address. The
      // identity is not ours to send: the API records it from the validated
      // token, which is why `acknowledgeSlaBreach` stopped accepting one.
      await acknowledgeSlaBreach(breach.datasetId, breach.breachDetectedAt);
      fetchData();
    } catch (err: any) {
      alert(`Failed to acknowledge: ${err.message ?? err}`);
    } finally {
      setAckLoading(null);
    }
  }

  return (
    <div className="space-y-4">
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
        <div className="flex flex-wrap items-end gap-4">
          <DateRangePicker
            label="Date range:"
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateFromChange={setDateFrom}
            onDateToChange={setDateTo}
          />
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Workspace</label>
            <select
              value={workspaceFilter}
              onChange={(e) => setWorkspaceFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-700 bg-white w-48"
            >
              <option value="">All workspaces</option>
              {workspaces.map((ws) => (
                <option key={ws.workspaceId} value={ws.workspaceId}>{ws.name}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Severity</label>
            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-700 bg-white w-32"
            >
              <option value="">All</option>
              <option value="critical">Critical</option>
              <option value="warning">Warning</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Acknowledged</label>
            <select
              value={ackFilter}
              onChange={(e) => setAckFilter(e.target.value as any)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-700 bg-white w-40"
            >
              <option value="all">All</option>
              <option value="unacknowledged">Unacknowledged</option>
              <option value="acknowledged">Acknowledged</option>
            </select>
          </div>
          <button
            onClick={fetchData}
            className="px-4 py-2 bg-blue-900 text-white text-sm font-medium rounded-lg hover:bg-blue-800 self-end"
          >
            Apply
          </button>
          <div className="self-end ml-auto">
            <ExportButton
              endpoint="/api/export/sla-breaches"
              params={{ date_from: dateFrom, date_to: dateTo, workspace_id: workspaceFilter || undefined }}
              filename="sla-breaches.csv"
            />
          </div>
        </div>
      </div>

      {loading && <Spinner />}
      {error && !loading && <ErrorBanner message={error} onRetry={fetchData} />}
      {!loading && !error && (
        <div>
          <p className="text-sm text-gray-500 mb-3">{breaches.length} breach{breaches.length !== 1 ? "es" : ""}</p>
          <div className="rounded-xl border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {["Date", "Dataset", "Workspace", "Expected By", "Last Actual Refresh", "Missed By", "Severity", "Acknowledged"].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {breaches.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-gray-400">No SLA breaches found.</td>
                  </tr>
                )}
                {breaches.map((b, i) => {
                  const key = `${b.datasetId}|${b.breachDetectedAt}`;
                  return (
                    <tr key={i} className={b.acknowledged ? "hover:bg-gray-50" : "bg-orange-50 hover:bg-orange-100"}>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{b.breachDate}</td>
                      <td className="px-4 py-3 font-medium text-gray-800">{b.datasetName}</td>
                      <td className="px-4 py-3 text-gray-500">{b.workspaceName}</td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDate(b.expectedRefreshBy)}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {b.lastActualRefresh === "never" || !b.lastActualRefresh
                          ? (
                            // "Never refreshed" was a claim about all of
                            // history made from a 48-hour read, and it was
                            // wrong for models that refreshed after the
                            // window closed. Say only what was looked for.
                            <span
                              className="text-gray-400 italic"
                              title={`No completed refresh found in the ${b.lookbackHours ?? 48} hours before this window closed. Earlier refreshes are on the artifact's Refresh tab.`}
                            >
                              None in prior {b.lookbackHours ?? 48}h
                            </span>
                          )
                          : formatDate(b.lastActualRefresh)}
                      </td>
                      <td className="px-4 py-3 text-orange-700 text-xs font-medium whitespace-nowrap">
                        {formatMissedBy(b.missedByMinutes)}
                      </td>
                      <td className="px-4 py-3">
                        <SeverityBadge level={b.severity ?? "warning"} />
                      </td>
                      <td className="px-4 py-3">
                        {b.acknowledged ? (
                          <div className="text-xs text-green-700">
                            <span className="font-bold">&#10003;</span> {b.acknowledgedBy}
                            {b.acknowledgedAt && <div className="text-gray-400">{formatDate(b.acknowledgedAt)}</div>}
                          </div>
                        ) : (
                          <button
                            onClick={() => handleAcknowledge(b)}
                            disabled={ackLoading === key}
                            className="px-2 py-1 text-xs rounded border border-orange-400 text-orange-700 hover:bg-orange-50 disabled:opacity-50"
                          >
                            {ackLoading === key ? "…" : "Acknowledge"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Calendar tab
// ---------------------------------------------------------------------------

function CalendarTab() {
  const [schedules, setSchedules] = useState<RefreshScheduleConfig[]>([]);
  const [selectedDatasetId, setSelectedDatasetId] = useState("");
  const [selectedDatasetName, setSelectedDatasetName] = useState("");

  useEffect(() => {
    fetchRefreshSchedules({ enabled_only: true })
      .then((data) => {
        setSchedules(data);
        if (data.length > 0 && !selectedDatasetId) {
          setSelectedDatasetId(data[0].datasetId);
          setSelectedDatasetName(data[0].datasetName);
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-gray-600">Dataset:</label>
        <select
          value={selectedDatasetId}
          onChange={(e) => {
            const found = schedules.find((s) => s.datasetId === e.target.value);
            setSelectedDatasetId(e.target.value);
            setSelectedDatasetName(found?.datasetName ?? "");
          }}
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-700 bg-white w-72"
        >
          <option value="">Select a dataset…</option>
          {schedules.map((s) => (
            <option key={s.datasetId} value={s.datasetId}>
              {s.datasetName} — {s.workspaceName}
            </option>
          ))}
        </select>
      </div>

      {selectedDatasetId ? (
        <div className="rounded-xl border border-gray-200 p-5">
          <RefreshCalendar datasetId={selectedDatasetId} datasetName={selectedDatasetName} />
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 px-6 py-12 text-center text-gray-400 text-sm">
          Select a dataset to view its refresh calendar.
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dataflows tab
// ---------------------------------------------------------------------------

function DataflowsTab({ workspaces }: { workspaces: Workspace[] }) {
  const dateFormat = useDateFormat();
  const formatDate = (value?: string) => formatDateTime(value, dateFormat);

  const [records, setRecords] = useState<DataflowRefreshRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState(sevenDaysAgo());
  const [dateTo, setDateTo] = useState(today());
  const [workspaceFilter, setWorkspaceFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);
    const params: Record<string, string | number> = { limit: 500 };
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (workspaceFilter) params.workspace_id = workspaceFilter;
    if (statusFilter) params.status = statusFilter;

    fetchDataflowRefreshes(params)
      .then(setRecords)
      .catch((err) => setError(err.message ?? "Failed to load dataflow refreshes."))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo, workspaceFilter, statusFilter]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, []);

  return (
    <div className="space-y-4">
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
        <div className="flex flex-wrap items-end gap-4">
          <DateRangePicker
            label="Date range:"
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateFromChange={setDateFrom}
            onDateToChange={setDateTo}
          />
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Workspace</label>
            <select
              value={workspaceFilter}
              onChange={(e) => setWorkspaceFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-700 bg-white w-48"
            >
              <option value="">All workspaces</option>
              {workspaces.map((ws) => (
                <option key={ws.workspaceId} value={ws.workspaceId}>{ws.name}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-700 bg-white w-32"
            >
              {/* These are the dataflow transactions API's words, stored
                  verbatim, and the filter is an exact match on the stored
                  value — so they cannot be relabelled to agree with the
                  refresh-history tab above ("Completed", "Unknown") without
                  the option returning nothing. In-progress was missing
                  entirely, which left a running dataflow refresh visible in
                  the table but impossible to isolate. */}
              <option value="">All</option>
              <option value="Success">Success</option>
              <option value="Failed">Failed</option>
              <option value="InProgress">In progress</option>
            </select>
          </div>
          <button
            onClick={fetchData}
            className="px-4 py-2 bg-blue-900 text-white text-sm font-medium rounded-lg hover:bg-blue-800 self-end"
          >
            Apply
          </button>
          <div className="self-end ml-auto">
            <ExportButton
              endpoint="/api/export/dataflow-refreshes"
              params={{ date_from: dateFrom, date_to: dateTo, workspace_id: workspaceFilter || undefined }}
              filename="dataflow-refreshes.csv"
            />
          </div>
        </div>
      </div>

      {loading && <Spinner />}
      {error && !loading && <ErrorBanner message={error} onRetry={fetchData} />}
      {!loading && !error && (
        <div>
          <p className="text-sm text-gray-500 mb-3">{records.length} record{records.length !== 1 ? "s" : ""}</p>
          <div className="rounded-xl border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {["Dataflow", "Workspace", "Started", "Status", "Duration", "Type", "Error"].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {records.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-gray-400">No dataflow refresh records found.</td>
                  </tr>
                )}
                {records.map((r, i) => {
                  const isFailed = (r.refreshStatus ?? "").toLowerCase() === "failed";
                  return (
                    <tr key={i} className={isFailed ? "bg-red-50" : "hover:bg-gray-50"}>
                      <td className="px-4 py-3 font-medium text-gray-800">{r.dataflowName}</td>
                      <td className="px-4 py-3 text-gray-500">{r.workspaceName}</td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{formatDate(r.refreshStartTime)}</td>
                      <td className="px-4 py-3"><StatusBadge status={r.refreshStatus} /></td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{formatDuration(r.durationSeconds)}</td>
                      <td className="px-4 py-3 text-gray-500">{r.refreshType || "—"}</td>
                      <td className="px-4 py-3 max-w-xs">
                        <ErrorExpander message={r.errorMessage} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function RefreshHealthPage() {
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const { workspaces, showPersonal, setShowPersonal } = useWorkspaceList({ monitoredOnly: true });

  // Overview data (pre-fetched)
  const [overviewRecords, setOverviewRecords] = useState<RefreshRecord[]>([]);
  const [overviewBreaches, setOverviewBreaches] = useState<SlaBreachRecord[]>([]);
  const [overviewDataflowRecords, setOverviewDataflowRecords] = useState<DataflowRefreshRecord[]>([]);
  const [overviewLoading, setOverviewLoading] = useState(false);

  const account = useAccount();
  const userEmail = account?.username ?? "unknown";


  useEffect(() => {
    if (activeTab !== "overview") return;
    setOverviewLoading(true);
    const sevenAgo = sevenDaysAgo();
    const params = { date_from: sevenAgo, limit: "500" };

    Promise.all([
      fetchRefreshHealth(params).catch(() => []),
      fetchSlaBreaches({}).catch(() => []),
      fetchDataflowRefreshes({ date_from: sevenAgo, limit: 200 }).catch(() => []),
    ]).then(([records, breaches, dataflowRecords]) => {
      setOverviewRecords(records as unknown as RefreshRecord[]);
      setOverviewBreaches(breaches as SlaBreachRecord[]);
      setOverviewDataflowRecords(dataflowRecords as DataflowRefreshRecord[]);
    }).finally(() => setOverviewLoading(false));
  }, [activeTab]);

  async function handleAcknowledgeFromOverview(breach: SlaBreachRecord) {
    try {
      // See handleAcknowledge — the third parameter is a note, not an identity.
      await acknowledgeSlaBreach(breach.datasetId, breach.breachDetectedAt);
      setOverviewBreaches((prev) =>
        prev.map((b) =>
          b.datasetId === breach.datasetId && b.breachDetectedAt === breach.breachDetectedAt
            ? { ...b, acknowledged: true, acknowledgedBy: userEmail }
            : b
        )
      );
    } catch (err: any) {
      alert(`Failed to acknowledge: ${err.message ?? err}`);
    }
  }

  return (
    <div className="space-y-5">
      {/* Page header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Refresh Health</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Dataset refresh history, SLA compliance monitoring, and dataflow refresh tracking.
          </p>
        </div>
        <ShowPersonalToggle show={showPersonal} onChange={setShowPersonal} />
      </div>

      {/* Tab bar */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-1">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-blue-600 text-blue-700"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === "overview" && (
        overviewLoading ? <Spinner /> :
        <OverviewTab
          records={overviewRecords}
          slaBreaches={overviewBreaches}
          dataflowRecords={overviewDataflowRecords}
          onAcknowledge={handleAcknowledgeFromOverview}
        />
      )}
      {activeTab === "history" && <HistoryTab workspaces={workspaces} />}
      {activeTab === "schedules" && <SchedulesTab workspaces={workspaces} />}
      {activeTab === "sla-breaches" && <SlaBreachesTab workspaces={workspaces} />}
      {activeTab === "calendar" && <CalendarTab />}
      {activeTab === "dataflows" && <DataflowsTab workspaces={workspaces} />}
    </div>
  );
}
